import React,{useState,useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import Layout from '../components/layout/Layout';
import StatCard from '../components/common/StatCard';
import {BarChart,Bar,XAxis,YAxis,CartesianGrid,Tooltip,ResponsiveContainer,AreaChart,Area,PieChart,Pie,Cell} from 'recharts';
import api from '../services/api';
import {fmtCurrency,fmtDate,safeArray} from '../utils/helpers';
import {useAuth} from '../hooks/useAuth';
import {getCurrencyCode,getCurrencySymbol} from '../utils/locale';

const COLORS=['#2563eb','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899'];
const GENDER_LABEL:Record<string,string>={MALE:'Male',FEMALE:'Female',OTHER:'Other'};

export default function DashboardPage(){
  const {t}=useTranslation();
  const {user}=useAuth();
  const [stats,setStats]=useState<any>(null);
  const [revenueData,setRevenueData]=useState<any[]>([]);
  const [genderData,setGenderData]=useState<any[]>([]);
  const [opdData,setOpdData]=useState<any[]>([]);
  const [extraStats,setExtraStats]=useState<any>({});
  const [recentPatients,setRecentPatients]=useState<any[]>([]);
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    const load=async()=>{
      setLoading(true);
      const today=new Date();
      const sevenDaysAgo=new Date();sevenDaysAgo.setDate(today.getDate()-6);
      const sixMonthsAgo=new Date();sixMonthsAgo.setMonth(today.getMonth()-5);sixMonthsAgo.setDate(1);
      const fmt=(d:Date)=>d.toISOString().split('T')[0];

      const results=await Promise.allSettled([
        api.getDashboardSummary(),
        api.getRevenueReport({from:fmt(sixMonthsAgo),to:fmt(today)}),
        api.getPatientStats(),
        api.getOPDReport({from:fmt(sevenDaysAgo),to:fmt(today)}),
        api.getLabStats(),
        api.getRadiologyStats(),
        api.getPharmacyStats(),
        api.getBillingStats(),
        api.getPatients({skip:0,take:5}),
      ]);

      const [summaryR,revenueR,patientStatsR,opdR,labR,radR,pharmR,billR,patientsR]=results;

      if(summaryR.status==='fulfilled')setStats(summaryR.value.data);

      // Revenue by month (group raw daily rows into months)
      if(revenueR.status==='fulfilled'){
        const rows=safeArray(revenueR.value.data);
        const byMonth:Record<string,number>=Object.fromEntries(
          Array.from({length:6},(_,i)=>{const d=new Date();d.setMonth(d.getMonth()-(5-i));return [d.toLocaleString('en',{month:'short'}),0];})
        );
        for(const r of rows){
          const m=new Date(r.date).toLocaleString('en',{month:'short'});
          if(m in byMonth) byMonth[m]+=parseFloat(r.total_collected||0);
        }
        setRevenueData(Object.entries(byMonth).map(([month,revenue])=>({month,revenue})));
      }

      // Gender breakdown
      if (patientStatsR.status === 'fulfilled') {
        const byGender = safeArray(patientStatsR.value.data, ['byGender']);
      
        const normalized = byGender.reduce((acc: any, item: any) => {
          const gender = (item.gender || 'UNKNOWN').toUpperCase();
      
          acc[gender] = (acc[gender] || 0) + Number(item.count);
      
          return acc;
        }, {});
      
        setGenderData(
          Object.entries(normalized).map(([gender, count]) => ({
            name: GENDER_LABEL[gender] || gender,
            value: count,
          }))
        );
      }
      // OPD last 7 days
      if(opdR.status==='fulfilled'){
        const rows=safeArray(opdR.value.data);
        const byDay:Record<string,number>=Object.fromEntries(
          Array.from({length:7},(_,i)=>{const d=new Date();d.setDate(d.getDate()-(6-i));return [d.toLocaleString('en',{weekday:'short'}),0];})
        );
        for(const r of rows){
          const d=new Date(r.date).toLocaleString('en',{weekday:'short'});
          if(d in byDay) byDay[d]+=parseInt(r.consultations||0);
        }
        setOpdData(Object.entries(byDay).map(([day,patients])=>({day,patients})));
      }

      setExtraStats({
        labToday: labR.status==='fulfilled'?labR.value.data?.todayRequests:0,
        radToday: radR.status==='fulfilled'?radR.value.data?.todayOrders:0,
        pharmacyToday: pharmR.status==='fulfilled'?pharmR.value.data?.todayRevenue:0,
        pendingBills: billR.status==='fulfilled'?billR.value.data?.pendingAmount:0,
      });

      if(patientsR.status==='fulfilled'){
        setRecentPatients(safeArray(patientsR.value.data,['patients']));
      }

      setLoading(false);
    };
    load();
  },[]);

  return(
    <Layout title={t('nav.dashboard','Dashboard')}>
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label={t('dashboard.totalPatients','Total Patients')} value={stats?.patients?.total??0} icon="👥" color="blue" sub={t('dashboard.todayCount','+{{count}} today',{count:stats?.patients?.today??0})}/>
        <StatCard label={t('dashboard.todaysAppointments',"Today's Appointments")} value={stats?.appointments?.today_pending??0} icon="📅" color="green" sub={t('dashboard.pendingConsultations','Pending consultations')}/>
        <StatCard label={t('dashboard.admittedPatients','Admitted Patients')} value={stats?.ipd?.admitted??0} icon="🛏️" color="amber" sub={t('dashboard.activeIpdCases','Active IPD cases')}/>
        <StatCard label={t('dashboard.todaysCollection',"Today's Collection")} value={fmtCurrency(stats?.billing?.today_collection??0)} icon="💰" color="purple" sub={t('dashboard.monthLabel','Month: {{amount}}',{amount:fmtCurrency(stats?.billing?.month_collection??0)})}/>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <div className="lg:col-span-2 card p-5">
          <h3 className="section-title">{t('dashboard.monthlyRevenue','Monthly Revenue ({{currency}}) — Last 6 Months',{currency:getCurrencyCode()})}</h3>
          {revenueData.every(d=>d.revenue===0)?(
            <div className="h-[220px] flex items-center justify-center text-slate-400 text-sm">{t('dashboard.noBillingData','No billing data yet')}</div>
          ):(
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={revenueData}>
              <defs><linearGradient id="rv" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2563eb" stopOpacity={0.15}/><stop offset="95%" stopColor="#2563eb" stopOpacity={0}/></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="month" tick={{fontSize:12}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:12}} axisLine={false} tickLine={false} tickFormatter={v=>`${getCurrencySymbol()}${(v/1000).toFixed(0)}K`}/>
              <Tooltip formatter={(v:any)=>fmtCurrency(v)}/>
              <Area type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2} fill="url(#rv)"/>
            </AreaChart>
          </ResponsiveContainer>
          )}
        </div>
        <div className="card p-5">
          <h3 className="section-title">{t('dashboard.patientsByGender','Patients by Gender')}</h3>
          {genderData.length===0?(
            <div className="h-[180px] flex items-center justify-center text-slate-400 text-sm">{t('dashboard.noPatientsYet','No patients yet')}</div>
          ):(
          <>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart><Pie data={genderData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3}>
              {genderData.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
            </Pie><Tooltip/></PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 mt-2">
            {genderData.map((d,i)=>(
              <div key={d.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{background:COLORS[i%COLORS.length]}}/><span className="text-slate-600">{d.name}</span></div>
                <span className="font-semibold text-slate-700">{d.value}</span>
              </div>
            ))}
          </div>
          </>
          )}
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <div className="card p-5">
          <h3 className="section-title">{t('dashboard.opdLast7Days','OPD — Last 7 Days')}</h3>
          {opdData.every(d=>d.patients===0)?(
            <div className="h-[160px] flex items-center justify-center text-slate-400 text-sm">{t('dashboard.noOpdData','No OPD consultations yet')}</div>
          ):(
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={opdData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="day" tick={{fontSize:11}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11}} axisLine={false} tickLine={false}/>
              <Tooltip/>
              <Bar dataKey="patients" fill="#10b981" radius={[4,4,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
          )}
        </div>

        <div className="card p-5">
          <h3 className="section-title">{t('dashboard.quickActions','Quick Actions')}</h3>
          <div className="grid grid-cols-2 gap-2.5">
            {[
              {label:t('dashboard.registerPatient','Register Patient'),  icon:'➕', path:'/patients',   module:'REGISTRATION', bg:'bg-blue-50 text-[#02357A] hover:bg-blue-100'},
              {label:t('dashboard.bookAppointment','Book Appointment'),  icon:'📅', path:'/appointments',module:null,           bg:'bg-green-50 text-green-700 hover:bg-green-100'},
              {label:t('dashboard.opdQueue','OPD Queue'),         icon:'🏥', path:'/opd',        module:'OPD',          bg:'bg-amber-50 text-amber-700 hover:bg-amber-100'},
              {label:t('dashboard.admitPatient','Admit Patient'),     icon:'🛏️', path:'/ipd/admit',  module:'IPD',          bg:'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'},
              {label:t('dashboard.newBill','New Bill'),          icon:'💳', path:'/billing',    module:'BILLING',      bg:'bg-purple-50 text-purple-700 hover:bg-purple-100'},
              {label:t('dashboard.labRequest','Lab Request'),       icon:'🔬', path:'/laboratory', module:'LAB',          bg:'bg-teal-50 text-teal-700 hover:bg-teal-100'},
              {label:t('dashboard.radiology','Radiology'),         icon:'🩻', path:'/radiology',  module:'RADIOLOGY',    bg:'bg-sky-50 text-sky-700 hover:bg-sky-100'},
              {label:t('dashboard.pharmacyDispense','Pharmacy Dispense'), icon:'💊', path:'/pharmacy',   module:'PHARMACY',     bg:'bg-orange-50 text-orange-700 hover:bg-orange-100'},
              {label:t('dashboard.bloodBank','Blood Bank'),        icon:'🩸', path:'/bloodbank',  module:'BLOOD_BANK',   bg:'bg-rose-50 text-rose-700 hover:bg-rose-100'},
              {label:t('dashboard.emergency','Emergency'),         icon:'🚨', path:'/emergency',  module:'EMERGENCY',    bg:'bg-red-50 text-red-700 hover:bg-red-100'},
              {label:t('dashboard.approvalInbox','Approval Inbox'),    icon:'✅', path:'/approvals',  module:null,           bg:'bg-green-50 text-green-700 hover:bg-green-100'},
              {label:t('dashboard.otSurgery','OT Surgery'),        icon:'🔪', path:'/ot',         module:'OT',           bg:'bg-slate-50 text-slate-700 hover:bg-slate-100'},
            ].filter(a=>{
              if(a.module===null) return true;
              if(!user?.accessibleModules) return false;
              if(user.accessibleModules==='*') return true;
              return (user.accessibleModules as string[]).includes(a.module);
            }).slice(0,8).map(a=>(
              <a key={a.label} href={a.path} className={`${a.bg} p-3 rounded-xl text-center cursor-pointer transition-all`}>
                <span className="text-xl block mb-1">{a.icon}</span>
                <span className="text-xs font-semibold">{a.label}</span>
              </a>
            ))}
          </div>
        </div>

        <div className="card p-5">
          <h3 className="section-title">{t('dashboard.todaysSummary',"Today's Summary")}</h3>
          <div className="space-y-2.5">
            {[
              {label:t('dashboard.opdConsultations','OPD Consultations'),value:stats?.opd?.today??0,icon:'🏥',color:'text-[#02357A]'},
              {label:t('dashboard.ipdAdmissions','IPD Admissions'),value:stats?.ipd?.admitted??0,icon:'🛏️',color:'text-green-600'},
              {label:t('dashboard.labRequests','Lab Requests'),value:extraStats.labToday??0,icon:'🔬',color:'text-purple-600'},
              {label:t('dashboard.radiologyOrders','Radiology Orders'),value:extraStats.radToday??0,icon:'🩻',color:'text-amber-600'},
              {label:t('dashboard.pharmacySales','Pharmacy Sales'),value:fmtCurrency(extraStats.pharmacyToday??0),icon:'💊',color:'text-[#039089]'},
              {label:t('dashboard.pendingBills','Pending Bills'),value:fmtCurrency(extraStats.pendingBills??0),icon:'⚠️',color:'text-red-600'},
            ].map(i=>(
              <div key={i.label} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2"><span>{i.icon}</span><span className="text-slate-600">{i.label}</span></div>
                <span className={`font-bold ${i.color}`}>{i.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recently Registered Patients */}
      <div className="card p-5">
        <h3 className="section-title">{t('dashboard.recentlyRegistered','Recently Registered Patients')}</h3>
        {loading?(
          <div className="py-8 text-center"><div className="w-6 h-6 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"/></div>
        ):recentPatients.length===0?(
          <div className="py-8 text-center text-slate-400 text-sm">{t('dashboard.noPatientsRegistered','No patients registered yet — click "Register Patient" above to add your first patient.')}</div>
        ):(
          <div className="space-y-2">
            {recentPatients.map((p:any)=>(
              <div key={p.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-[#015CA6] flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                    {(p.firstName||'').charAt(0)}{(p.lastName||'').charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">{p.firstName} {p.lastName}</p>
                    <p className="text-xs font-mono text-slate-400">{p.uhid}</p>
                  </div>
                </div>
                <span className="text-xs text-slate-400">{fmtDate(p.createdAt)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
